/**
 * ActionHistoryPanel — vertical sidebar showing the action history of the
 * current hand, grouped by street.
 *
 * Matches wireframe `04_active_hand_desktop.excalidraw` right sidebar:
 *   ACTION PANEL    LIVE · READ ONLY
 *   PREFLOP
 *     · BTN raises $10
 *     · BB calls $10
 *   FLOP
 *     · BB checks
 *     · BTN bets $20
 *     · BB calls $20
 *   TURN
 *     ...
 *
 * V1 first cut: preflop entries are synthesized from session pot type +
 * resolved position (preflop is auto-resolved in V1 per PRD §8.1, so the
 * engine's history doesn't record those actions). When real preflop play
 * lands in a later version, this synthesis logic gets removed.
 *
 * Full PRD §9.1 "decision tree" mode (per-node interaction in V2) is not in
 * this slice — V1 ships read-only.
 */

import { type Card } from '@/types/card';
import type { ActionRecord, HandState } from '@/types/handState';
import type { ResolvedHandConfig } from '@/types/session';
import { cn } from '@/lib/utils';
import { MobileDrawer } from './MobileDrawer';

const SUIT_GLYPH: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };
const SUIT_COLOR: Record<string, string> = {
  s: 'text-suit-spade',
  c: 'text-suit-club',
  h: 'text-suit-heart',
  d: 'text-suit-diamond',
};

const STREETS: Array<{ key: 'preflop' | 'flop' | 'turn' | 'river'; label: string }> = [
  { key: 'preflop', label: 'PREFLOP' },
  { key: 'flop', label: 'FLOP' },
  { key: 'turn', label: 'TURN' },
  { key: 'river', label: 'RIVER' },
];

interface ActionHistoryPanelProps {
  state: HandState;
  resolved: ResolvedHandConfig | null;
  /** Whether the mobile drawer is open. Desktop sidebar ignores this. */
  isOpen?: boolean;
  /** Tap the collapsed "PANEL" strip to open the mobile drawer. */
  onOpen?: () => void;
  /** Close the mobile drawer (tap scrim, tap close, etc.). */
  onClose?: () => void;
}

export function ActionLog({
  state,
  resolved,
  isOpen = false,
  onOpen,
  onClose,
}: ActionHistoryPanelProps) {
  return (
    <>
      <aside className="flex w-10 shrink-0 flex-col overflow-hidden border-l border-border bg-surface-app sm:w-64">
        {/* Mobile-only collapsed strip — tappable to open the drawer. */}
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'flex flex-1 items-center justify-center sm:hidden',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-border-focus',
          )}
          aria-label="Open action panel"
          aria-expanded={isOpen}
        >
          <span
            className="text-[10px] font-bold uppercase tracking-[0.3em] text-text-muted"
            style={{ writingMode: 'vertical-rl' }}
          >
            Panel
          </span>
        </button>

        {/* Desktop: full action panel content rendered inline */}
        <div className="hidden flex-1 flex-col overflow-hidden sm:flex">
          <div className="border-b border-border bg-surface-elevated px-3 py-2">
            <div className="text-xs font-bold uppercase tracking-wider text-text-secondary">
              Action panel
            </div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">
              Live · read only
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2">
            <ActionHistoryBody state={state} resolved={resolved} />
          </div>
        </div>
      </aside>

      {/* Mobile drawer — slides in from the right when isOpen */}
      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose ?? (() => {})}
        side="right"
        title="Action panel"
        subtitle="Live · read only"
      >
        <div className="px-3 py-2">
          <ActionHistoryBody state={state} resolved={resolved} />
        </div>
      </MobileDrawer>
    </>
  );
}

/**
 * The street-grouped action list. Rendered both in the desktop sidebar and
 * inside the mobile drawer body.
 */
function ActionHistoryBody({
  state,
  resolved,
}: {
  state: HandState;
  resolved: ResolvedHandConfig | null;
}) {
  const heroIP = state.heroPosition === 'IP';
  const heroPosLabel = heroIP ? 'BTN' : 'BB';
  const villainPosLabel = heroIP ? 'BB' : 'BTN';
  const bb = state.blinds.bb;

  // Synthesize preflop history per V1 PRD §8.1 (preflop auto-resolved).
  const preflopActions: SynthAction[] =
    state.potType === 'SRP'
      ? [
          { actor: heroPosLabel, verb: 'raises', amount: 3 * bb },
          { actor: villainPosLabel, verb: 'calls', amount: 3 * bb },
        ]
      : [
          // 3BP: fish opens, hero 3-bets, fish calls
          { actor: villainPosLabel, verb: 'raises', amount: 3 * bb },
          { actor: heroPosLabel, verb: '3-bets', amount: 9 * bb },
          { actor: villainPosLabel, verb: 'calls', amount: 9 * bb },
        ];

  return (
    <>
      {STREETS.map((street) => {
        // Only show streets that have happened or are happening
        const streetIndex = STREETS.findIndex((s) => s.key === street.key);
        const currentIndex = STREETS.findIndex((s) => s.key === state.street);
        if (streetIndex > currentIndex) return null;

        // Preflop is synthesized; postflop streets come from state.history.
        const actions =
          street.key === 'preflop'
            ? preflopActions
            : state.history
                .filter((r) => r.street === street.key)
                .map((r) => actionRecordToSynth(r, heroIP));

        // Cards dealt at street start (turn/river)
        const dealtCard: Card | undefined =
          street.key === 'turn' && state.board.length >= 4
            ? state.board[3]
            : street.key === 'river' && state.board.length >= 5
              ? state.board[4]
              : undefined;

        return (
          <StreetBlock
            key={street.key}
            label={street.label}
            dealtCard={dealtCard}
            actions={actions}
            resolved={resolved}
          />
        );
      })}
    </>
  );
}

// =============================================================================
// Street block
// =============================================================================

interface SynthAction {
  actor: string;       // e.g., 'BTN', 'BB'
  verb: string;        // e.g., 'raises', 'calls', 'checks', 'folds'
  amount?: number;
}

function StreetBlock({
  label,
  dealtCard,
  actions,
  resolved: _resolved,
}: {
  label: string;
  dealtCard?: Card;
  actions: SynthAction[];
  resolved: ResolvedHandConfig | null;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">
        <span>{label}</span>
        {dealtCard && (
          <span className={cn('text-xs', SUIT_COLOR[dealtCard.suit])}>
            {dealtCard.rank}
            {SUIT_GLYPH[dealtCard.suit]}
          </span>
        )}
      </div>
      <ul className="space-y-0.5">
        {actions.map((a, i) => (
          <li
            key={i}
            className="text-[10px] leading-tight text-text-secondary sm:text-xs"
          >
            <span className="text-text-muted">·</span>{' '}
            <span className="font-medium text-text-primary">{a.actor}</span>{' '}
            {a.verb}
            {a.amount !== undefined && (
              <span className="text-text-secondary"> ${a.amount}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function actionRecordToSynth(record: ActionRecord, heroIP: boolean): SynthAction {
  const heroPos = heroIP ? 'BTN' : 'BB';
  const villainPos = heroIP ? 'BB' : 'BTN';
  const actor = record.actor === 'hero' ? heroPos : villainPos;
  const a = record.action;
  switch (a.kind) {
    case 'check':
      return { actor, verb: 'checks' };
    case 'fold':
      return { actor, verb: 'folds' };
    case 'call':
      return { actor, verb: 'calls', amount: record.chipsCommitted };
    case 'bet':
      return { actor, verb: 'bets', amount: a.total };
    case 'raise':
      return { actor, verb: 'raises to', amount: a.total };
    case 'allin':
      return { actor, verb: 'all-in', amount: record.chipsCommitted };
  }
}
